import { usePage } from "@inertiajs/inertia-react";
import { Users } from "react-feather";
import { AccordionItem } from "@/Components";
import React, { useMemo } from "react";
import TeamsReportedDataTable from "@/Components/Dashboard/TeamsReportedDataTable";
import TeamsReportedText from "@/Components/Dashboard/TeamsReportedText";
import { QUESTION_TYPES } from "@/Enums/question_types";
import {
  convertToHumanReadable,
  convertWeightToHumanReadable,
} from "@/Utilities/Utilities";
import {
  chartColors,
  chartTextColors,
  DashboardPieChart,
} from "@/Utilities/V2Graphs";
import ShrinkableTagRow from "@/Components/ShrinkableTagRow";
import ResultTypeIcon from "@/Components/Questions/ResultTypeIcon";

const QuestionAnswers = ({ question, count }) => {
  const answers = usePage().props?.answers || {};
  const answer = answers?.[question.id] || { teams: {} };

  const RenderEmissionInfo = ({
    readableAnswer,
    showPartialResult = false,
  }) => {
    if (!readableAnswer) return null;

    const readableSum = readableAnswer.sum
      ? convertToHumanReadable(parseFloat(readableAnswer.sum))
      : "";

    if (
      showPartialResult &&
      (readableAnswer.dataset?.[0]?.unit === "%" ||
        readableAnswer.dataset?.[0]?.unit === "")
    ) {
      // Get the dataset object with the largest y value
      const largestValue = readableAnswer.dataset.reduce((prev, current) =>
        prev.y > current.y ? prev : current,
      );

      const isPercentage = readableAnswer.dataset?.[0]?.unit === "%";

      if (isPercentage) {
        return (
          <span className="font-bold">{`${largestValue.y.toFixed(1)}% - ${
            largestValue.x
          }`}</span>
        );
      }

      return (
        <span className="font-bold">
          {`${largestValue.percentage.toFixed(1)}% (${convertToHumanReadable(
            largestValue.y,
          )}) - ${largestValue.x}`}
        </span>
      );
    }

    return answer.has_emission ? (
      <>
        <span className="font-bold">{`${
          convertWeightToHumanReadable(parseFloat(readableAnswer.total))?.concat
        } CO₂e`}</span>
        {readableAnswer.sum && (
          <>
            {readableSum} {readableAnswer.units}
          </>
        )}
      </>
    ) : (
      readableAnswer.sum && (
        <span className="font-bold">{`${readableSum} ${readableAnswer.units}`}</span>
      )
    );
  };

  const TitleRight = ({ readable_answer }) => {
    return (
      <div className="mr-6 flex gap-2 text-typo max-md:hidden">
        {readable_answer?.label && (
          <span className="rounded-sm bg-primary-light px-2 py-[2px] text-sm">
            {readable_answer.label}
          </span>
        )}
        <ResultTypeIcon resultType={question?.result_type} />
        <RenderEmissionInfo
          readableAnswer={readable_answer}
          showPartialResult={true}
        />

        {question.tags.length > 0 && (
          <div className="mx-4 max-w-[200px]">
            <ShrinkableTagRow tags={question.tags} showMinimumTags={1} />
          </div>
        )}

        <Users className="ml-3" />
        <span className="font-bold">{count}</span>
      </div>
    );
  };

  // build const dataSets = based on answer.readable_answers.dataset that's currently containing many arrays with one x and y value
  const dataSets = useMemo(
    () =>
      answer?.readable_answers?.dataset?.map((item, i) => ({
        label: item.x,
        data: [item.y],
        backgroundColor: chartColors[i],
        textColor: chartTextColors[i],
        unit: item.unit,
      })) || [],
    [answer],
  );

  return (
    <AccordionItem
      key={question.id}
      title={question.title}
      background="bg-gray-light"
      titleStyle="font-bold"
      titleright={<TitleRight readable_answer={answer?.readable_answers} />}
      chevron={false}
    >
      <>
        {Object.keys(answer?.teams).length > 0 &&
          (question?.type?.enum === QUESTION_TYPES.Historical ||
            question?.type?.enum === QUESTION_TYPES.Calculate ||
            question?.type?.enum === QUESTION_TYPES.Select ||
            question?.type?.enum === QUESTION_TYPES.Distribute) && (
            <div className="grid-rows-[repeat(2, auto)] grid w-full grid-cols-3 gap-2 pb-8 pt-4 md:grid-rows-1 md:gap-8">
              <div className="col-span-3 self-start p-2 md:col-span-2 md:p-4">
                <p className="mb-2 flex">
                  <ResultTypeIcon resultType={question?.result_type} />
                  &nbsp;
                  {question?.result_type?.charAt(0).toUpperCase() +
                    question?.result_type?.slice(1)}
                </p>

                <div className="mb-2 flex flex-col">
                  <RenderEmissionInfo
                    readableAnswer={answer?.readable_answers}
                  />
                </div>

                {answer?.readable_answers?.dataset.map((item, i) => (
                  <div key={i} className="flex justify-between gap-2">
                    <p>
                      <span
                        className="inline-block h-3 w-3 rounded-[2px]"
                        style={{ backgroundColor: chartColors[i] }}
                      />
                      &nbsp;
                      {item.x}
                    </p>
                    <p className="">
                      {convertToHumanReadable(item.y)} {item.unit}
                      {item.unit !== "%" && (
                        <>&nbsp;({item.percentage.toFixed(1)}%)</>
                      )}
                    </p>
                  </div>
                ))}
              </div>

              <div className="col-span-3 flex items-center justify-center md:col-span-1">
                {dataSets.length > 0 && (
                  <DashboardPieChart
                    dataSets={dataSets}
                    size={{ width: 192, height: 192 }}
                    className="max-w-full"
                    emission={answer.has_emission}
                  />
                )}
              </div>
            </div>
          )}

        {question?.type?.enum === QUESTION_TYPES.FreeText && (
          <TeamsReportedText teamsAnswers={answer.teams} question={question} />
        )}

        {(question?.type?.enum === QUESTION_TYPES.Historical ||
          question?.type?.enum === QUESTION_TYPES.Calculate ||
          question?.type?.enum === QUESTION_TYPES.Select ||
          question?.type?.enum === QUESTION_TYPES.Distribute) && (
          <AccordionItem
            title="Teams"
            titleStyle="font-bold text-sm"
            className="border"
            chevron={false}
          >
            <TeamsReportedDataTable
              teamsAnswers={answer.teams}
              question={question}
            />
          </AccordionItem>
        )}

        <div className="mt-4 flex gap-2">
          {question.tags.map((tag) => (
            <span
              key={tag.id}
              className="bg-gray-lighter px-2 py-1 leading-none text-typo-inactive"
            >
              {tag.name}
            </span>
          ))}
        </div>
      </>
    </AccordionItem>
  );
};

export default QuestionAnswers;
